import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";

export type AuthorityBucket = 1 | 2 | 3;

export interface AuthorityAnswers {
  appointment: string;
  trust: string;
  appointment_name?: string;
  trust_name?: string;
  authority_bucket?: AuthorityBucket;
}

const TRUSTEE_ANSWER = "me-trustee";

const VALID_APPOINTMENTS = new Set(["me", "someone-else", "not-yet", "not-sure"]);
const VALID_TRUSTS = new Set([TRUSTEE_ANSWER, "someone-else-trustee", "no-trust", "not-sure"]);

export function deriveAuthorityBucket(
  appointment: string,
  trust: string,
): AuthorityBucket | undefined {
  if (!VALID_APPOINTMENTS.has(appointment) || !VALID_TRUSTS.has(trust)) return undefined;
  if (appointment === "me" || trust === TRUSTEE_ANSWER) return 2;
  if (appointment === "someone-else" || trust === "someone-else-trustee") return 3;
  return 1;
}

export function getAuthorityBucket(
  answers: AuthorityAnswers | null | undefined,
): AuthorityBucket | undefined {
  return (
    answers?.authority_bucket ??
    (answers ? deriveAuthorityBucket(answers.appointment, answers.trust) : undefined)
  );
}

export function isTrusteeAuthority(answers: AuthorityAnswers | null | undefined): boolean {
  return answers?.trust === TRUSTEE_ANSWER;
}

export function useAuthorityAnswers(estateId: number | undefined) {
  const queryClient = useQueryClient();

  const { data: savedAnswers, isFetched } = useQuery({
    queryKey: ["authorityAnswers", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_tasks")
        .select("answers")
        .eq("estate_id", estateId!)
        .eq("task_id", TASK_IDS.LOT)
        .maybeSingle();
      return (data?.answers as AuthorityAnswers | null) ?? null;
    },
    enabled: !!estateId,
    staleTime: 0,
  });

  const saveAnswers = useMutation({
    mutationFn: async (answers: AuthorityAnswers) => {
      if (!estateId) return;

      const { data: existing } = await supabase
        .from("estate_tasks")
        .select("answers")
        .eq("estate_id", estateId)
        .eq("task_id", TASK_IDS.LOT)
        .maybeSingle();

      const mergedAnswers = {
        ...((existing?.answers as Record<string, unknown>) ?? {}),
        ...answers,
      };

      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: TASK_IDS.LOT,
          status: "in_progress",
          answers: mergedAnswers,
          last_active_at: new Date().toISOString(),
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authorityAnswers", estateId] });
    },
  });

  return {
    savedAnswers,
    isFetched,
    saveAnswers: saveAnswers.mutateAsync,
  };
}
